// WindowEventAdapter — Default implementation for global window/document event listeners
// Injected into VNInvestCharts to avoid direct window/document access in hooks
// All global access is lazy-initialized inside the factory function for tree-shaking
export const createDefaultWindowEventAdapter = () => {
    let isAttached = false;
    const attachedListeners = [];
    const attach = () => {
        if (isAttached || typeof window === 'undefined')
            return;
        isAttached = true;
    };
    const ensureAttached = () => {
        if (!isAttached)
            attach();
    };
    return {
        addGlobalListeners: (listeners) => {
            ensureAttached();
            const cleanupFunctions = [];
            if (listeners.onPointerUp) {
                const handler = listeners.onPointerUp;
                window.addEventListener('pointerup', handler);
                cleanupFunctions.push(() => window.removeEventListener('pointerup', handler));
            }
            if (listeners.onPointerCancel) {
                const handler = listeners.onPointerCancel;
                window.addEventListener('pointercancel', handler);
                cleanupFunctions.push(() => window.removeEventListener('pointercancel', handler));
            }
            if (listeners.onPointerMove) {
                const handler = listeners.onPointerMove;
                window.addEventListener('pointermove', handler);
                cleanupFunctions.push(() => window.removeEventListener('pointermove', handler));
            }
            if (listeners.onMouseUp) {
                const handler = listeners.onMouseUp;
                window.addEventListener('mouseup', handler);
                cleanupFunctions.push(() => window.removeEventListener('mouseup', handler));
            }
            if (listeners.onTouchEnd) {
                const handler = listeners.onTouchEnd;
                window.addEventListener('touchend', handler);
                cleanupFunctions.push(() => window.removeEventListener('touchend', handler));
            }
            if (listeners.onTouchCancel) {
                const handler = listeners.onTouchCancel;
                window.addEventListener('touchcancel', handler);
                cleanupFunctions.push(() => window.removeEventListener('touchcancel', handler));
            }
            if (listeners.onContextMenu) {
                const handler = listeners.onContextMenu;
                window.addEventListener('contextmenu', handler);
                cleanupFunctions.push(() => window.removeEventListener('contextmenu', handler));
            }
            if (listeners.onLostPointerCapture) {
                const handler = listeners.onLostPointerCapture;
                window.addEventListener('lostpointercapture', handler);
                cleanupFunctions.push(() => window.removeEventListener('lostpointercapture', handler));
            }
            if (listeners.onPageHide) {
                const handler = listeners.onPageHide;
                window.addEventListener('pagehide', handler);
                cleanupFunctions.push(() => window.removeEventListener('pagehide', handler));
            }
            if (listeners.onBlur) {
                const handler = listeners.onBlur;
                window.addEventListener('blur', handler);
                cleanupFunctions.push(() => window.removeEventListener('blur', handler));
            }
            if (listeners.onVisibilityChange) {
                const handler = listeners.onVisibilityChange;
                if (typeof document !== 'undefined') {
                    document.addEventListener('visibilitychange', handler);
                    cleanupFunctions.push(() => document.removeEventListener('visibilitychange', handler));
                }
            }
            return () => {
                cleanupFunctions.forEach(cleanup => cleanup());
            };
        },
    };
};
// Singleton - lazy initialized on first access
let _defaultWindowEventAdapter = null;
export const getDefaultWindowEventAdapter = () => {
    if (!_defaultWindowEventAdapter) {
        _defaultWindowEventAdapter = createDefaultWindowEventAdapter();
    }
    return _defaultWindowEventAdapter;
};
// Backward compatibility - lazy getter
export const defaultWindowEventAdapter = new Proxy({}, {
    get(target, prop) {
        const adapter = getDefaultWindowEventAdapter();
        return adapter[prop];
    }
});
export const createMockWindowEventAdapter = () => {
    let mockCleanup = null;
    const adapter = {
        addGlobalListeners: () => {
            adapter.addGlobalListenersCalls++;
            return mockCleanup ?? (() => { });
        },
        addGlobalListenersCalls: 0,
        resetCalls: () => { adapter.addGlobalListenersCalls = 0; },
        setMockCleanup: (c) => { mockCleanup = c; },
    };
    return adapter;
};
